import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

// Public VAPID-nyckel (säker att exponera på klienten)
const VAPID_PUBLIC_KEY =
  "BOq8GcB2aZBfCx_EBehBtfIE2TbnRkcu-fOVYOfOrjBb_XUjP9pLcPwOByK41m4VSiTOUjv-M0fOmJVCNJBc5UU";

/**
 * Hook för att hantera push-notiser i webbläsaren.
 * - Registrerar service worker
 * - Begär notistillstånd från användaren
 * - Skapar/tar bort prenumeration i databasen
 *
 * VIKTIGT: Service worker registreras INTE i Lovable preview/iframe.
 */
export function usePushNotifications() {
  const { toast } = useToast();
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  // Kolla om push-notiser stöds + om användaren redan är prenumererad
  useEffect(() => {
    const checkSupport = async () => {
      const supported =
        typeof window !== "undefined" &&
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        "Notification" in window;

      setIsSupported(supported);

      if (!supported) return;

      setPermission(Notification.permission);

      // Kolla om vi redan har en prenumeration
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) {
          const sub = await reg.pushManager.getSubscription();
          setIsSubscribed(!!sub);
        }
      } catch (e) {
        console.error("Could not check push subscription:", e);
      }
    };

    checkSupport();
  }, []);

  /** Aktivera push-notiser på den här enheten */
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      toast({
        title: "Stöds inte i din webbläsare",
        description: "Testa Chrome, Edge eller Firefox. På iPhone måste du först lägga till HittaDelar på hemskärmen.",
        variant: "destructive",
      });
      return false;
    }

    setLoading(true);
    try {
      // Registrera service worker
      const registration = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;

      // Be om tillstånd
      const perm = await Notification.requestPermission();
      setPermission(perm);

      if (perm !== "granted") {
        toast({
          title: "Notiser blockerade",
          description: "Du måste tillåta notiser i webbläsarens inställningar för att få push-notiser.",
          variant: "destructive",
        });
        return false;
      }

      // Skapa prenumeration
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
      });

      const json = subscription.toJSON();

      // Spara i databasen
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({ title: "Du måste vara inloggad", variant: "destructive" });
        return false;
      }

      const { error } = await supabase.from("push_subscriptions").upsert(
        {
          user_id: user.id,
          endpoint: subscription.endpoint,
          p256dh: json.keys?.p256dh || "",
          auth: json.keys?.auth || "",
          user_agent: navigator.userAgent,
          last_used_at: new Date().toISOString(),
        },
        { onConflict: "user_id,endpoint" }
      );

      if (error) throw error;

      // Säkerställ att inställningar finns
      await supabase.from("push_notification_preferences").upsert(
        { user_id: user.id },
        { onConflict: "user_id", ignoreDuplicates: true }
      );

      setIsSubscribed(true);
      toast({
        title: "🔔 Push-notiser aktiverade!",
        description: "Du får nu notiser direkt på den här enheten.",
      });
      return true;
    } catch (error: any) {
      console.error("Push subscribe error:", error);
      toast({
        title: "Kunde inte aktivera notiser",
        description: error.message || "Försök igen senare.",
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported, toast]);

  /** Stäng av push-notiser på den här enheten */
  const unsubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        setIsSubscribed(false);
        return true;
      }

      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        const endpoint = subscription.endpoint;
        await subscription.unsubscribe();

        // Ta bort från databasen
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from("push_subscriptions")
            .delete()
            .eq("user_id", user.id)
            .eq("endpoint", endpoint);
        }
      }

      setIsSubscribed(false);
      toast({
        title: "Push-notiser avstängda",
        description: "Du får inga fler notiser på den här enheten.",
      });
      return true;
    } catch (error: any) {
      console.error("Push unsubscribe error:", error);
      toast({
        title: "Kunde inte stänga av",
        description: error.message,
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  return {
    isSupported,
    permission,
    isSubscribed,
    loading,
    subscribe,
    unsubscribe,
  };
}

/** Konvertera VAPID public key från base64 till Uint8Array */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
